import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { loginMicrosoft } from "../../api/AUTH/auth";
import microsoftLogo from "../../assets/logos_officiels/microsoft.svg";

const MS_CLIENT_ID   = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
const MS_TENANT_ID   = process.env.REACT_APP_MICROSOFT_TENANT_ID;
const MS_REDIRECT    = window.location.origin;
const MS_AUTH_URL    = `https://login.microsoftonline.com/${MS_TENANT_ID}/oauth2/v2.0/authorize`;
const MS_TOKEN_URL   = `https://login.microsoftonline.com/${MS_TENANT_ID}/oauth2/v2.0/token`;
const MS_SCOPES      = "User.Read offline_access openid email profile";

function randomBase64url() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  return btoa(String.fromCharCode(...arr)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

async function sha256Base64url(str) {
  const data   = new TextEncoder().encode(str);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export default function MicrosoftButton() {
  const { fetchUser, setIsLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    try {
      // 1. Générer PKCE
      const codeVerifier  = randomBase64url();
      const codeChallenge = await sha256Base64url(codeVerifier);
      const state         = randomBase64url();

      // 2. Construire l'URL d'autorisation
      const authParams = new URLSearchParams({
        client_id:             MS_CLIENT_ID,
        response_type:         'code',
        redirect_uri:          MS_REDIRECT,
        scope:                 MS_SCOPES,
        code_challenge:        codeChallenge,
        code_challenge_method: 'S256',
        state,
      });

      // 3. Ouvrir le popup Microsoft
      const popup = window.open(
        `${MS_AUTH_URL}?${authParams}`,
        'ms-login',
        'width=600,height=700,left=300,top=100'
      );
      if (!popup) throw new Error("Le popup a été bloqué par le navigateur.");

      // 4. Attendre le code via postMessage (envoyé par index.js dans le popup)
      const code = await new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
          window.removeEventListener('message', handler);
          reject(new Error("Délai de connexion dépassé."));
        }, 120_000);

        function handler(event) {
          if (event.origin !== window.location.origin) return;
          if (event.data?.type !== 'MS_AUTH_CODE') return;
          clearTimeout(timer);
          window.removeEventListener('message', handler);
          if (event.data.state !== state) { reject(new Error("CSRF state invalide")); return; }
          resolve(event.data.code);
        }
        window.addEventListener('message', handler);
      });

      // 5. Échanger le code contre les tokens (retourne access_token + refresh_token)
      const tokenRes = await fetch(MS_TOKEN_URL, {
        method:  'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          client_id:     MS_CLIENT_ID,
          grant_type:    'authorization_code',
          code,
          redirect_uri:  MS_REDIRECT,
          code_verifier: codeVerifier,
          scope:         MS_SCOPES,
        }).toString(),
      });
      const tokens = await tokenRes.json();
      if (!tokenRes.ok) throw new Error(tokens.error_description || "Erreur échange de token Microsoft");

      const { access_token, refresh_token } = tokens;

      // 6. Récupérer l'email depuis Microsoft Graph
      const graphRes  = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: { Authorization: `Bearer ${access_token}` },
      });
      const graphUser = await graphRes.json();
      const email     = graphUser.mail || graphUser.userPrincipalName;

      // 7. Authentifier auprès du backend
      await loginMicrosoft(access_token, refresh_token, email);

      await fetchUser();
      setIsLoggedIn(true);
      toast.success("Connexion Microsoft réussie !");
      navigate("/home", { replace: true });
    } catch (error) {
      console.error("Erreur connexion Microsoft :", error);
      toast.error(error.message || "Erreur lors de la connexion Microsoft");
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleLogin}
      disabled={loading}
      className="mx-auto"
      style={{ 
        width: '100%',
        maxWidth: '280px',
        height: '44px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontSize: '15px',
        fontWeight: '500',
        backgroundColor: '#fff',
        color: '#202124',
        border: '1px solid #dadce0',
        borderRadius: '6px',
        padding: '10px 28px',
        gap: '10px',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.6 : 1,
        transition: 'all 0.3s ease',
        boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}
      onMouseOver={(e) => !loading && (e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.12)')}
      onMouseOut={(e) => !loading && (e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.08)')}
    >
      <img
        src={microsoftLogo}
        alt="Microsoft logo"
        style={{ width: '20px', height: '20px' }}
      />
      {loading ? 'Connexion...' : 'Se connecter avec Microsoft'}
    </button>
  );
}
